import { httpsCallable } from 'firebase/functions';
import { functions } from '../config';
import { apiKeyIpMode, type ApiKeyIpMode } from '../../../constants/enum';
import type {
  ApiKeyCreatedResponse,
  ApiKeyEnvironmentType,
  ApiKeyListItem,
  ApiKeyRotatedResponse,
} from '../../../types/apiKey';

interface CallableSuccess<T> {
  success: boolean;
  data: T;
}

interface ListApiKeysParams {
  company_id?: string;
}

interface ListApiKeysResponseItem {
  api_key_id: string;
  company_id: string;
  display_name: string;
  environment: ApiKeyEnvironmentType;
  key_last4: string;
  status: number;
  ip_mode?: ApiKeyIpMode; // 未設定の既存キーは inherit 扱い（#2982）
  ip_allowlist: string[];
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  created_at: string;
  created_by: string;
  rotated_from: string | null;
  rotated_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
}

const toDate = (iso: string | null): Date | null => (iso ? new Date(iso) : null);

const fromListResponse = (raw: ListApiKeysResponseItem): ApiKeyListItem => ({
  api_key_id: raw.api_key_id,
  company_id: raw.company_id,
  display_name: raw.display_name,
  environment: raw.environment,
  key_last4: raw.key_last4,
  status: raw.status,
  ip_mode: raw.ip_mode ?? apiKeyIpMode.inherit,
  ip_allowlist: raw.ip_allowlist ?? [],
  expires_at: toDate(raw.expires_at),
  last_used_at: toDate(raw.last_used_at),
  last_used_ip: raw.last_used_ip,
  created_at: toDate(raw.created_at) ?? new Date(),
  created_by: raw.created_by,
  rotated_from: raw.rotated_from,
  rotated_at: toDate(raw.rotated_at),
  revoked_at: toDate(raw.revoked_at),
  revoked_by: raw.revoked_by,
});

/**
 * APIキー一覧を取得
 */
export const listApiKeys = async (companyId?: string): Promise<ApiKeyListItem[]> => {
  const callable = httpsCallable<ListApiKeysParams, CallableSuccess<ListApiKeysResponseItem[]>>(
    functions,
    'listApiKeys',
  );
  const result = await callable({ company_id: companyId });
  return result.data.data.map(fromListResponse);
};

interface CreateApiKeyParams {
  company_id?: string;
  display_name: string;
  environment: ApiKeyEnvironmentType;
  expires_at?: string | null;
  ip_allowlist?: string[];
}

/**
 * APIキーを新規発行
 */
export const createApiKey = async (params: CreateApiKeyParams): Promise<ApiKeyCreatedResponse> => {
  const callable = httpsCallable<CreateApiKeyParams, CallableSuccess<ApiKeyCreatedResponse>>(
    functions,
    'createApiKey',
  );
  const result = await callable(params);
  return result.data.data;
};

interface RotateApiKeyParams {
  company_id?: string;
  api_key_id: string;
  grace_period_minutes: number;
}

/**
 * APIキーを再生成
 */
export const rotateApiKey = async (params: RotateApiKeyParams): Promise<ApiKeyRotatedResponse> => {
  const callable = httpsCallable<RotateApiKeyParams, CallableSuccess<ApiKeyRotatedResponse>>(
    functions,
    'rotateApiKey',
  );
  const result = await callable(params);
  return result.data.data;
};

interface RevokeApiKeyParams {
  company_id?: string;
  api_key_id: string;
}

/**
 * APIキーを即時無効化
 */
export const revokeApiKey = async (
  params: RevokeApiKeyParams,
): Promise<{ api_key_id: string; already_revoked?: boolean; revoked_at?: string }> => {
  const callable = httpsCallable<
    RevokeApiKeyParams,
    CallableSuccess<{ api_key_id: string; already_revoked?: boolean; revoked_at?: string }>
  >(functions, 'revokeApiKey');
  const result = await callable(params);
  return result.data.data;
};

interface UpdateApiKeyIpAllowlistParams {
  company_id?: string;
  api_key_id: string;
  ip_allowlist: string[];
  /** IP 制限モード（#2982）。省略時は従来挙動（リストあり=custom / なし=inherit） */
  ip_mode?: ApiKeyIpMode;
}

/**
 * APIキーごとのIP制限（モード + ホワイトリスト）を更新
 */
export const updateApiKeyIpAllowlist = async (
  params: UpdateApiKeyIpAllowlistParams,
): Promise<{ api_key_id: string; ip_allowlist: string[]; ip_mode: ApiKeyIpMode }> => {
  const callable = httpsCallable<
    UpdateApiKeyIpAllowlistParams,
    CallableSuccess<{ api_key_id: string; ip_allowlist: string[]; ip_mode: ApiKeyIpMode }>
  >(functions, 'updateApiKeyIpAllowlist');
  const result = await callable(params);
  return result.data.data;
};

interface SetCompanyApiEnabledParams {
  company_id: string;
  enabled: boolean;
  /** 有効化時のみ必須: YYYY-MM-DD 形式の利用開始日（日本時間） */
  start_date?: string;
  note?: string;
}

/**
 * 運営スタッフ専用: 企業のAPI機能をON/OFF
 */
export const setCompanyApiEnabled = async (
  params: SetCompanyApiEnabledParams,
): Promise<{ before: boolean; after: boolean }> => {
  const callable = httpsCallable<
    SetCompanyApiEnabledParams,
    CallableSuccess<{ before: boolean; after: boolean }>
  >(functions, 'setCompanyApiEnabled');
  const result = await callable(params);
  return result.data.data;
};
